import { ModeEdit, RestartAlt } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Grid,
  IconButton,
  Popover,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import { ActionType, DataType, EditingMode, SortingMode, Table, useTable } from "ka-table";
import { ChildComponent } from "ka-table/Models/ChildComponent";
import { ICellProps, ICellTextProps } from "ka-table/props";
import "ka-table/style.css";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import {
  DisplayEmailAddress,
  DisplayPhoneNumber,
} from "../../../ManageCandidates/Components/CandidateRow/CandidateRow";
import { CandidateStatusIcon, CandidateStatusPicker, getStatusTooltip } from "../CandidateStatus";
import { CandidateStatusColor } from "../../Enums/CandidateStatusColor";
import { downloadFile } from "../../Queries/CandidateQueries";
import { useCandidateStatusDefinitionsQuery } from "../../Queries/CandidateStatusQueries";
import { useAuthToken } from "../../State/Hooks/authHooks";
import { useNotification } from "../../State/Hooks/notificationHooks";
import { useAppSelector } from "../../State/Hooks/reduxHooks";
import { selectClientValue } from "../../State/Slices/SelectedClientSlice";
import { useUserProfileStore } from "../../State/Stores/UserProfileStore";
import { ICandidateAttachment, ICandidateEmailAddress, ICandidatePhoneNumber } from "../../Typings/Candidate";
import { ICandidateNotePartial } from "../../Typings/CandidateProfile";
import { ITablePagination } from "../../Typings/SortableTable";
import { KaColumn } from "../../Typings/TableColumn";
import { STORAGE_VERSION_DATE } from "../../Utilities/browserStorage";
import { ExportButton as ExportButtonComponent } from "../Export/ExportButton";
import { useSortableTableState } from "./State/SortableTableState";
import "./Styles/TableStyles.css";
import CrmTableColumnSelection from "./Subcomponents/CrmTableColumnSelection";

export interface RowSelectionProps {
  onRowSelect: (row: any, selected: boolean) => void;
  isRowSelected: (row: any) => boolean;
  onAllSelected: (selected: boolean) => void;
}

export interface ICrmTableProps {
  rowId?: string;
  name: string;
  columns: KaColumn[];
  data: any[];
  pagination: ITablePagination;
  loading: boolean;
  onSort?: (property: string) => void;
  order?: "asc" | "desc" | undefined;
  orderBy?: string;
  exportUrlOrCallback?: string | (() => void);
  ExportButton?: (enabledColumns: KaColumn[]) => React.ReactNode;
  onRowClick?: (row: any) => void;
  rowSelection?: RowSelectionProps;
  isRowDisabled?: (row: any) => boolean;
  rowTitle?: string | ((row: any) => string);
  rowTestId?: (row: any) => string;
  footerActions?: ReactNode;
  isAllSelected?: boolean;
  disableSelectAll?: boolean;
  customCell?: ChildComponent<ICellProps<any>>;
}

export const DEFAULT_SELECTION_COLUMN: KaColumn = {
  key: "selection-cell",
  isFilterable: false,
  width: 75,
  isResizable: false,
  type: "select",
};

export default function CrmTable({
  name,
  columns,
  data,
  onSort,
  pagination,
  loading,
  order,
  orderBy,
  exportUrlOrCallback,
  ExportButton,
  onRowClick,
  rowSelection,
  isRowDisabled,
  rowTitle,
  rowId = "id",
  rowTestId,
  footerActions,
  isAllSelected,
  disableSelectAll = false,
  customCell,
}: ICrmTableProps) {
  const notification = useNotification();
  const token = useAuthToken();
  const selectedClient = useAppSelector(selectClientValue);
  const localStorageKey = buildLocalStorageKey(name, selectedClient.clientId);
  const { data: statusDefinitions } = useCandidateStatusDefinitionsQuery();
  const [statusPickerTarget, setStatusPickerTarget] = useState<{
    candidateId: string;
    statusColor: string;
  } | null>(null);

  const baselineCols = useMemo(() => {
    const savedCols = readLocalStorageColumns(localStorageKey);
    const base = savedCols.length ? savedCols : columns;

    const selectionCellIndex = base.findIndex((c) => c.key === DEFAULT_SELECTION_COLUMN.key);
    const hasSelectionCell = selectionCellIndex !== -1;

    if (!rowSelection) {
      return hasSelectionCell
        ? base.map((col, index) => (index === selectionCellIndex ? { ...col, visible: false } : col))
        : base;
    }
    return hasSelectionCell ? base : [DEFAULT_SELECTION_COLUMN, ...base];
  }, [localStorageKey, columns, rowSelection]);

  const [mergedColumns, setMergedColumns] = useState<KaColumn[]>(baselineCols);

  const table = useTable({
    onDispatch: async (action, newState) => {
      const { data, ...settings } = newState;
      if (settings.columns.length) {
        localStorage.setItem(
          localStorageKey,
          JSON.stringify(
            settings.columns.map((c) => ({
              ...c,
              visible: c.visible ?? true,
              sortDirection: c.sortDirection,
            })),
          ),
        );
      }
      if (action.type === ActionType.UpdateSortDirection) {
        onSort?.(action.columnKey);
      }
    },
  });

  const {
    getters: { rowsPerPage, page, editTableColumnsOpen },
    handlers: { handleChangePage, handleChangeRowsPerPage },
    setters: { setEditTableColumnsOpen },
  } = useSortableTableState(data, columns, name, loading, pagination, orderBy, true);

  const handleChangeCurrentPage =
    pagination.enabled && pagination.handleChangePage ? pagination.handleChangePage : handleChangePage;
  const handleChangeRows =
    pagination.enabled && pagination.handleChangeRowsPerPage
      ? pagination.handleChangeRowsPerPage
      : handleChangeRowsPerPage;

  const currentPage = pagination.enabled && !loading ? (pagination.currentPage ?? page) : page;
  const currentPageSize = pagination.enabled && !loading ? (pagination.rows ?? rowsPerPage) : rowsPerPage;

  const [allSelected, setAllSelected] = useState<boolean>(false);

  useEffect(() => {
    if (isAllSelected !== undefined && isAllSelected !== allSelected) {
      setAllSelected(isAllSelected);
    }
  }, [isAllSelected, allSelected]);

  const SelectionCell = ({ rowData }: ICellTextProps) => {
    return (
      <Checkbox
        size="small"
        checked={rowSelection?.isRowSelected(rowData) ?? false}
        onChange={(e) => {
          rowSelection?.onRowSelect(rowData, e.currentTarget.checked);
        }}
        inputProps={{ "aria-label": "controlled" }}
        disabled={isRowDisabled?.(rowData)}
      />
    );
  };

  const SelectionHeader = () => {
    return (
      <Checkbox
        size="small"
        data-test="select-all-checkbox"
        checked={allSelected}
        disabled={disableSelectAll}
        onChange={(e) => {
          if (!disableSelectAll) {
            setAllSelected(e.currentTarget.checked);
            rowSelection?.onAllSelected(e.currentTarget.checked);
          }
        }}
      />
    );
  };

  const { getUserAdjustedDate } = useUserProfileStore();

  const formatData = (column: KaColumn, value: any) => {
    if (column.dataType === DataType.Date) {
      return value && getUserAdjustedDate(value).format("MM/DD/YYYY h:mm A");
    } else if (column.dataType === DataType.Object) {
      if (column.key === "emailAddresses") {
        return value.map((e: ICandidateEmailAddress) => (
          <DisplayEmailAddress key={e.emailAddress} email={e} />
        ));
      } else if (column.key === "phoneNumbers") {
        return value.map((e: ICandidatePhoneNumber) => <DisplayPhoneNumber key={e.phoneNumber} phone={e} />);
      } else if (column.key === "locations" && column.type === "default") {
        return value?.map((e: { name: string }) => e.name).join(",");
      } else if (column.key === "tags" && column.type === "default") {
        return <TagPopover tags={value} />;
      } else if (column.key === "attachments" && column.type === "default") {
        return value && value.length > 0 ? (
          <AttachmentDownload
            token={token}
            clientId={selectedClient.clientId}
            attachments={value as ICandidateAttachment[]}
            onFailure={() => notification.displayError("Failed to download file.")}
          />
        ) : (
          <></>
        );
      } else if (column.key === "notes" && column.type === "default") {
        return <NotesPopover notes={value as ICandidateNotePartial[]} />;
      } else if (value && Array.isArray(value)) {
        return value.join(", ");
      }
      return value;
    }

    return value;
  };

  return (
    <>
      <div className="crm-table" data-test={name} key={localStorageKey}>
        <Table
          key={localStorageKey}
          table={table}
          columns={mergedColumns}
          data={data}
          editingMode={EditingMode.None}
          sortingMode={SortingMode.SingleRemote}
          rowKeyField={rowId}
          sort={({ column }) => onSort?.(column.key)}
          format={({ column, value }) => formatData(column, value)}
          columnReordering
          columnResizing
          loading={{
            enabled: loading,
            text: "Loading data",
          }}
          noData={{
            text: "No data to display.",
          }}
          childComponents={{
            cellText: rowSelection
              ? {
                  content: (props) => {
                    if (props.column.key === DEFAULT_SELECTION_COLUMN.key) {
                      return <SelectionCell {...props} />;
                    }
                  },
                }
              : undefined,
            headCell: rowSelection
              ? {
                  content: (props) => {
                    if (props.column.key === DEFAULT_SELECTION_COLUMN.key) {
                      return <SelectionHeader />;
                    }
                  },
                }
              : undefined,
            cell: customCell
              ? customCell
              : {
                  elementAttributes: (props) => ({
                    onClick: (_e, extendedEvent) => {
                      const { childProps } = extendedEvent;
                      if (
                        childProps.column.key !== "actions" &&
                        childProps.column.key !== DEFAULT_SELECTION_COLUMN.key &&
                        childProps.column.key !== "tags" &&
                        childProps.column.key !== "notes" &&
                        !isRowDisabled?.(childProps.rowData)
                      ) {
                        onRowClick?.(childProps.rowData);
                      }
                    },
                    "data-test": `item-${props.column.key}-${props.rowData.id}`,
                  }),
                  content: (props) => {
                    if (props.column.key === "fullName") {
                      const statusColor = props.rowData.statusColor ?? CandidateStatusColor.White;
                      return (
                        <Stack direction="row" gap={1} alignItems="center">
                          <span>{props.rowData.fullName}</span>
                          <CandidateStatusIcon
                            statusColor={statusColor}
                            tooltip={getStatusTooltip(statusColor, statusDefinitions)}
                            onClick={(e) => {
                              e.stopPropagation();
                              setStatusPickerTarget({
                                candidateId: props.rowData.id,
                                statusColor,
                              });
                            }}
                          />
                        </Stack>
                      );
                    }
                    if (props.column.key.startsWith("__")) {
                      const customKey = props.column.key.replace("__", "");
                      return formatData({ ...props.column, key: customKey }, props.value);
                    }
                  },
                },
            dataRow: {
              elementAttributes: (props) => ({
                title: rowTitle
                  ? typeof rowTitle === "string"
                    ? rowTitle
                    : rowTitle(props.rowData)
                  : undefined,
                "data-test": rowTestId?.(props.rowData),
              }),
            },
          }}
        />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 2, p: 1 }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <Button
              onClick={() => setEditTableColumnsOpen(true)}
              size="small"
              title="Edit Table Columns"
              variant="outlined"
              color="primary"
              aria-label="edit columns button"
              startIcon={<ModeEdit color="primary" />}
            >
              Edit Columns
            </Button>

            {ExportButton?.(mergedColumns)}

            {exportUrlOrCallback && (
              <ExportButtonComponent
                exportUrlOrCallback={exportUrlOrCallback}
                label="Export"
                title="Export Data"
              />
            )}

            {footerActions}

            <IconButton
              area-label="Reset Table"
              title="Reset Table"
              onClick={() => {
                localStorage.removeItem(localStorageKey);
                window.location.reload();
              }}
            >
              <RestartAlt />
            </IconButton>
          </Stack>

          <TablePagination
            rowsPerPageOptions={pagination.rowsPerPage ?? [10, 25, 50, 100]}
            component="div"
            count={pagination.totalCount ?? data.length}
            rowsPerPage={currentPageSize}
            page={currentPage}
            onPageChange={handleChangeCurrentPage}
            onRowsPerPageChange={handleChangeRows}
            data-test="tablePagination"
            labelRowsPerPage={pagination.labelRowsPerPage}
            labelDisplayedRows={pagination.labelDisplayedRows}
            sx={{ "& .MuiToolbar-root": { minHeight: 0, paddingLeft: 0 }, "& p": { marginY: 0 } }}
          />
        </Stack>
      </div>

      <CrmTableColumnSelection
        columns={mergedColumns}
        handleModalClose={() => setEditTableColumnsOpen(false)}
        isOpen={editTableColumnsOpen}
        onCheckboxChange={(e, checked) => {
          const colType = e.target.getAttribute("data-column-type");
          const col = mergedColumns.find(
            (c) => c.title === e.target.value && (!colType || c.type === colType),
          );
          if (col) {
            checked ? table.showColumn(col.key) : table.hideColumn(col.key);
            setMergedColumns((prev) => prev.map((c) => (c.key === col.key ? { ...c, visible: checked } : c)));
          }
        }}
      />
      {statusPickerTarget && (
        <CandidateStatusPicker
          open
          onClose={() => setStatusPickerTarget(null)}
          candidateId={statusPickerTarget.candidateId}
          currentStatusColor={statusPickerTarget.statusColor}
        />
      )}
    </>
  );
}

function AttachmentDownload({
  token,
  clientId,
  attachments,
  onFailure,
}: {
  token: string;
  clientId: string;
  attachments: ICandidateAttachment[];
  onFailure: () => void;
}) {
  return (
    <>
      {attachments.map((attachment) => (
        <Button
          key={`attachment-${attachment.id}`}
          data-test={`candidate-attachment-${attachment.name}`}
          variant="text"
          onClick={async (ev: any) => {
            ev.stopPropagation();
            try {
              await downloadFile(
                token,
                `/candidateprofile/client/${clientId}/${attachment.candidateId}/attachments/${attachment.id}/download`,
                attachment.name,
              );
            } catch (e) {
              onFailure();
            }
          }}
        >
          {attachment.name}
        </Button>
      ))}
    </>
  );
}

function TagPopover({ tags }: { tags: string[] }) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "tag-popover" : undefined;

  return tags && tags.length > 0 ? (
    <>
      {tags.map((t: string, idx: number) => (
        <Chip
          key={`tag-${idx}-${t}`}
          label={t}
          size="small"
          sx={{ ml: 1 }}
          color="secondary"
          onClick={handleClick}
        />
      ))}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Grid container sx={{ maxWidth: 300, p: 1 }}>
          {tags.map((t: string, idx: number) => (
            <Grid key={`popover-item-${idx}-${t}`} item sx={{ m: 0.5 }}>
              <Chip key={idx} label={t} size="small" sx={{ ml: 1 }} color="secondary" />
            </Grid>
          ))}
        </Grid>
      </Popover>
    </>
  ) : (
    <></>
  );
}

function NotesPopover({ notes }: { notes: ICandidateNotePartial[] }) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "notes-popover" : undefined;

  const truncateText = (text?: string, maxLength: number = 100): string => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const getNotesExcerpt = (noteList: ICandidateNotePartial[]): string => {
    if (!noteList || noteList.length === 0) return "";
    const firstNote = noteList[0];
    const text = truncateText(firstNote?.note, 80);
    return noteList.length > 1 ? `${text} (+${noteList.length - 1} more)` : text;
  };

  if (!Array.isArray(notes)) {
    return <></>;
  }

  return notes && notes.length > 0 ? (
    <>
      <Typography
        variant="body2"
        onClick={handleClick}
        sx={{
          cursor: "pointer",
          maxWidth: "80ch",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textDecoration: open ? "underline" : "none",
          "&:hover": {
            textDecoration: "underline",
          },
        }}
      >
        {getNotesExcerpt(notes)}
      </Typography>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        slotProps={{}}
      >
        <Box sx={{ maxWidth: 400, p: 2 }}>
          {notes.map((note, idx) => (
            <Box key={`popover-note-${idx}-${note.id}`} sx={{ mb: idx < notes.length - 1 ? 2 : 0 }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {note.note}
              </Typography>
              {idx < notes.length - 1 && <Divider sx={{ mt: 1 }} />}
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  ) : (
    <></>
  );
}

export const buildLocalStorageKey = (tableName: string, clientId: string): string =>
  `${tableName}-table-state-${clientId}-${STORAGE_VERSION_DATE}`;

export const readLocalStorageColumns: (key: string) => KaColumn[] = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
};
